import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Calendar, Lock, AlertCircle, BarChart3, Users, Trophy, ExternalLink, Vote } from "lucide-react";
import { LanguageSelector } from "../LanguageSelector";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { API_URL } from "../../lib/config";

const CHART_COLORS = ["#2563eb", "#059669", "#d97706", "#dc2626", "#7c3aed", "#0891b2", "#db2777", "#65a30d", "#ea580c", "#6366f1"];

function formatDateTime(iso, tz) {
  if (!iso) return "-";
  const s = String(iso).trim();
  const isUTC = s.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(s);
  if (isUTC) {
    return new Date(s).toLocaleString("ca-ES", { timeZone: tz || "Europe/Madrid", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  }
  const parts = s.split('T');
  if (parts.length < 2) return iso;
  const [year, month, day] = parts[0].split('-');
  const [hour, minute] = parts[1].split(':');
  return `${(day||'').padStart(2,'0')}/${(month||'').padStart(2,'0')}/${year} ${(hour||'00').padStart(2,'0')}:${(minute||'00').padStart(2,'0')}`;
}

export function VotingErrorView({ error }) {
  const { t } = useTranslation();
  const err = error;
  const isNotStarted = err?.code === "not_started";
  const isEnded = err?.code === "ended" || err?.code === "not_active";
  const isScrutinized = err?.status === "scrutinized" && err?.results_summary;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="public-vote-ended">
      {(err?.entity_name || err?.entity_logo_url) && (
        <header className="bg-white border-b border-slate-200">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
            {err.entity_logo_url ? (
              <img
                src={`${API_URL}${err.entity_logo_url}`}
                alt={err.entity_name || ""}
                className="w-10 h-10 rounded-lg object-cover"
                data-testid="error-entity-logo"
              />
            ) : (
              <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center">
                <Vote className="h-5 w-5 text-white" />
              </div>
            )}
            <div className="flex-1">
              <h1 className="font-heading font-bold text-slate-900" data-testid="error-entity-name">{err.entity_name}</h1>
              <p className="text-xs text-slate-500">{t("voting2.votingSystem")}</p>
            </div>
            <LanguageSelector />
          </div>
        </header>
      )}
      <div className="flex-1 flex items-center justify-center p-4">
      <div className={`text-center w-full ${isScrutinized ? "max-w-2xl" : "max-w-md"}`}>
        <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${
          isNotStarted ? "bg-blue-100" : isScrutinized ? "bg-emerald-100" : isEnded ? "bg-amber-100" : "bg-slate-100"
        }`}>
          {isNotStarted ? <Calendar className="h-10 w-10 text-blue-500" />
            : isScrutinized ? <BarChart3 className="h-10 w-10 text-emerald-600" />
            : isEnded ? <Lock className="h-10 w-10 text-amber-500" />
            : <AlertCircle className="h-10 w-10 text-slate-400" />}
        </div>

        <h1 className="text-2xl font-heading font-bold text-slate-900 mb-2" data-testid="vote-page-title">
          {isNotStarted ? t('voting.notStartedYet') : isScrutinized ? t('voting.votingResults') : isEnded ? t('voting.votingEnded') : t('voting.consultaNotAvailable')}
        </h1>

        {err?.title && <p className="text-lg font-medium text-slate-700 mb-2" data-testid="consulta-title">{err.title}</p>}

        {isScrutinized && (err?.start_date || err?.end_date) && (
          <p className="text-sm text-slate-500 mb-4" data-testid="consulta-dates">
            {err.start_date && formatDateTime(err.start_date, err.entity_timezone)}
            {err.start_date && err.end_date && "  -  "}
            {err.end_date && formatDateTime(err.end_date, err.entity_timezone)}
          </p>
        )}

        {isScrutinized && <ScrutinizedResults err={err} />}

        {!isScrutinized && (isNotStarted || isEnded) && (err?.start_date || err?.end_date) && (
          <div className="bg-white border border-slate-200 rounded-xl p-5 text-left mt-4 space-y-3">
            {err.start_date && (
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center shrink-0"><Calendar className="h-4 w-4 text-green-600" /></div>
                <div><p className="text-xs text-slate-500">{t('voting.startDate')}</p><p className="font-medium text-slate-900">{formatDateTime(err.start_date, err.entity_timezone)}</p></div>
              </div>
            )}
            {err.end_date && (
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center shrink-0"><Calendar className="h-4 w-4 text-red-600" /></div>
                <div><p className="text-xs text-slate-500">{t('voting.endDate')}</p><p className="font-medium text-slate-900">{formatDateTime(err.end_date, err.entity_timezone)}</p></div>
              </div>
            )}
          </div>
        )}

        {!isNotStarted && !isEnded && <p className="text-slate-500">{t('voting.votingNotExists')}</p>}
      </div>
      </div>
    </div>
  );
}

function ScrutinizedResults({ err }) {
  const { t } = useTranslation();
  const results = err.results_summary;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
      className="mt-6 space-y-4" data-testid="results-summary-section">
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-center justify-center gap-3 mb-1"><Users className="h-5 w-5 text-slate-500" /><span className="text-sm text-slate-500">{t('results.totalVoters')}</span></div>
        <p className="text-3xl font-bold text-slate-900" data-testid="total-votes">{results.total_votes}</p>
      </div>

      {results.results?.length > 0 && (() => {
        const topVotes = results.results[0].votes;
        const tied = results.results.filter(r => r.votes === topVotes);
        const isTie = tied.length > 1;
        return (
          <div className={`${isTie ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'} border rounded-xl p-5`} data-testid="winner-card">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Trophy className={`h-5 w-5 ${isTie ? 'text-amber-600' : 'text-emerald-600'}`} />
              <span className={`text-sm font-medium ${isTie ? 'text-amber-700' : 'text-emerald-700'}`}>
                {isTie ? t('results.tie') : t('voting.mostVotedOption')}
              </span>
            </div>
            {isTie ? (
              <div className="space-y-1">
                {tied.map((r, i) => (
                  <p key={i} className="text-lg font-bold text-amber-900">{r.option} — {r.votes} {t('voting.votes')} ({r.percentage}%)</p>
                ))}
              </div>
            ) : (
              <>
                <p className="text-xl font-bold text-emerald-900">{results.results[0].option}</p>
                <p className="text-sm text-emerald-700 mt-1">{results.results[0].votes} {t('voting.votes')} ({results.results[0].percentage}%)</p>
              </>
            )}
          </div>
        );
      })()}

      {results.results?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="results-chart">
          <h3 className="text-sm font-semibold text-slate-700 mb-4 text-left">{t('results.distributionVotes')}</h3>
          <ResponsiveContainer width="100%" height={Math.max(200, results.results.length * 44)}>
            <BarChart data={results.results} layout="vertical" margin={{ top: 0, right: 40, left: 10, bottom: 0 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="option" width={160} tick={{ fontSize: 12, fill: "#475569" }} tickLine={false} axisLine={false} />
              <Tooltip formatter={(value, name, props) => [`${value} ${t('voting.votes')} (${props.payload.percentage}%)`, t('voting.votes')]}
                contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "13px" }} />
              <Bar dataKey="votes" radius={[0, 6, 6, 0]} barSize={24}>
                {results.results.map((_, index) => <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="results-table">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50">
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase">#</th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t('results.optionColumn')}</th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase text-right">{t('results.votesColumn')}</th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase text-right">%</th>
            </tr>
          </thead>
          <tbody>
            {results.results.map((r, i) => (
              <tr key={i} className={`border-b border-slate-50 ${i === 0 ? "bg-emerald-50/50" : ""}`}>
                <td className="px-4 py-3 text-sm text-slate-400 font-medium">{i + 1}</td>
                <td className="px-4 py-3 text-sm text-slate-900 font-medium">{r.option}</td>
                <td className="px-4 py-3 text-sm text-slate-700 text-right font-semibold">{r.votes}</td>
                <td className="px-4 py-3 text-sm text-slate-500 text-right">{r.percentage}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {results.scrutiny_date && (
        <p className="text-xs text-slate-400 mt-2" data-testid="scrutiny-date">
          {t('voting.scrutinyDoneOn', { date: new Date(results.scrutiny_date).toLocaleString(undefined, {
            timeZone: err?.entity_timezone || "Europe/Madrid", day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit"
          })})}
        </p>
      )}
    </motion.div>
  );
}
